import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {ScaleIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import {useNavigate} from "react-router-dom";
import DeleteTrailRating from '../Delete';
import TrailRatingContextMenu from "components/menus/TrailRatingContextMenu";
import {useAuth} from "context/AuthContext";
import NotAuthorised from "pages/NotAuthorised";
import type {TrailRating} from "types/TrailRating";
import {useOperator} from "../../../context/OperatorContext";

function TrailRatingList() {
  const navigate = useNavigate();
  const auth = useAuth();
  const {termFor} = useOperator();

  const [deletingTrailRating, setDeletingTrailRating] = useState<TrailRating | null>(null);
  const [page, setPage] = useState<number>(1);

  const result = useQuery(
    ['trail-ratings.all', {page}],
    () => ngage.trailRatings.filtered({page}),
    {keepPreviousData: true, enabled: auth.can('trail-ratings:read')}
  )

  // Prevent unauthorised viewing
  if (auth.cannot('trail-ratings:read')) {
    return <NotAuthorised/>
  }

  // Prefetch next and previous pages
  prefetchNearbyPages('trail-ratings.all', page, ngage.trailRatings.filtered)

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('trail-ratings:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/trail-ratings/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {'App Content': '/', 'Site': '/', [termFor('Trail') + ' Ratings']: '/trail-ratings'};

  return (
    <Page
      title={`${termFor('Trail')} Ratings`}
      actions={actions}
      breadcrumbs={breadcrumbs}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader>Colour</TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>Trails</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={5}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <ScaleIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No trail ratings</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((trailRating, index) => (
              <TableRow key={trailRating.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <TrailRatingContextMenu
                        trailRating={trailRating}
                        onDeleteTrailRating={() => setDeletingTrailRating(trailRating)}
                        deleteDisabled={trailRating.trail_count > 0}
                        position='left'
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <ScaleIcon className="h-5 w-5 mr-2 text-gray-500"/> {trailRating.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{truncate(trailRating.description, 50)}</TableData>
                <TableData>
                  <span
                    className="block border border-1 border-gray-400 rounded w-16 h-6"
                    style={{backgroundColor: trailRating.colour}}
                  />
                </TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{trailRating.trail_count}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <TrailRatingContextMenu
                    trailRating={trailRating}
                    onDeleteTrailRating={() => setDeletingTrailRating(trailRating)}
                    deleteDisabled={trailRating.trail_count > 0}
                  />
                </TableData>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {/* Delete confirmation modal */}
      <DeleteTrailRating
        trailRating={deletingTrailRating}
        show={!!deletingTrailRating}
        onCancel={() => setDeletingTrailRating(null)}
        onDelete={() => setDeletingTrailRating(null)}
        onDeleteFail={() => setDeletingTrailRating(null)}
      />
    </Page>
  );
}

export default TrailRatingList;